import {Box, Grid, OrderedList, Section, UnorderedList} from '@primer/react-brand/esm'
import {codeMarkRenderer} from '@github-ui/swp-core/components/contentful/shared/RichTextFieldRenderer'
import type {Options} from '@contentful/rich-text-react-renderer'
import {BLOCKS} from '@contentful/rich-text-types'
import type {OutroSectionProps} from './types'
import baseStyles from '../../../styles/base.module.css'
import styles from './OutroSection.module.css'
import {FlexSuiteOutroCTA} from '../../../components/FlexSuiteOutroCTA'
import {FlexSuiteOutroCustomerStories} from '../../../components/FlexSuiteOutroCustomerStories'
import {FlexSuiteOutroFaqs, getCompleteFaqGroups} from '../../../components/FlexSuiteOutroFaqs'
import {FlexSuiteOutroResources} from '../../../components/FlexSuiteOutroResources'
import {clsx} from 'clsx'
import {
  FlexSuiteTestimonialBanner,
  FlexSuiteTestimonialQuote,
  FlexSuiteTestimonialStat,
} from '../../../components/FlexSuiteTestimonial'
import {FlexSuiteStatistics} from '../../../components/FlexSuiteStatistics'
import {useBrandedDivider} from '../../../hooks/useBrandedDivider'

const faqAnswerRenderOptions = {
  renderMark: codeMarkRenderer,
  renderNode: {
    [BLOCKS.OL_LIST]: (_, children) => <OrderedList>{children}</OrderedList>,
    [BLOCKS.UL_LIST]: (_, children) => <UnorderedList>{children}</UnorderedList>,
    [BLOCKS.LIST_ITEM]: (_, children) => <UnorderedList.Item>{children}</UnorderedList.Item>,
  },
} satisfies Options

export const OutroSection = ({
  outroCustomerStories,
  outroCtaBanner,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroIncludeResources,
  outroResourcesHeading,
  outroResources,
  outroFaqs,
  includeFaqs,
  faqHeading,
  outroTestimonial,
  outroTestimonialLink,
  outroTestimonialLogo,
  outroTestimonialQuote,
  outroTestimonialImage,
  outroTestimonialLabel,
  outroTestimonialStats,
  outroTestimonialName,
  outroTestimonialTitle,
}: OutroSectionProps) => {
  const validFaqGroups = includeFaqs ? getCompleteFaqGroups(outroFaqs) : []

  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0
  const hasStats = outroTestimonial === 'Stats' && Boolean(outroTestimonialStats?.length)
  const hasFullStatsTestimonial =
    hasStats && Boolean(outroTestimonialQuote && outroTestimonialLink && outroTestimonialImage)
  const hasTestimonial =
    (outroTestimonial === 'Quote' && Boolean(outroTestimonialQuote)) ||
    hasStats ||
    (outroTestimonial === 'Brand Banner' && Boolean(outroTestimonialQuote))

  const {hasBrandedDivider, BrandedDivider} = useBrandedDivider()

  if (
    !hasTestimonial &&
    !hasCustomerStories &&
    !outroCtaBanner &&
    (!outroIncludeResources || !outroResources?.length) &&
    validFaqGroups.length === 0
  ) {
    return null
  }

  return (
    <Box marginBlockStart={48} className={baseStyles.greenBorderBottom}>
      <Section paddingBlockStart="none" className={baseStyles.gridFrameTop} paddingBlockEnd="none" fullWidth>
        {hasCustomerStories && <FlexSuiteOutroCustomerStories variant="icon-cards" stories={outroCustomerStories} />}

        {outroTestimonial === 'Quote' && outroTestimonialQuote ? (
          <FlexSuiteTestimonialQuote
            quote={outroTestimonialQuote}
            name={outroTestimonialName}
            title={outroTestimonialTitle}
            link={outroTestimonialLink && outroTestimonialLink.fields}
            logo={outroTestimonialLogo}
          />
        ) : null}

        {hasFullStatsTestimonial && outroTestimonialQuote && outroTestimonialLink && outroTestimonialImage ? (
          <Box marginBlockStart={48}>
            <FlexSuiteTestimonialStat
              quote={outroTestimonialQuote}
              link={outroTestimonialLink.fields}
              label={outroTestimonialLabel}
              image={outroTestimonialImage}
              stats={outroTestimonialStats}
            />
          </Box>
        ) : hasStats ? (
          <FlexSuiteStatistics stats={outroTestimonialStats} />
        ) : null}

        {outroTestimonial === 'Brand Banner' && outroTestimonialQuote ? (
          <Box paddingBlockEnd="spacious" paddingBlockStart="spacious">
            <FlexSuiteTestimonialBanner
              quote={outroTestimonialQuote}
              link={outroTestimonialLink && outroTestimonialLink.fields}
              logo={outroTestimonialLogo}
              image={outroTestimonialImage}
            />
          </Box>
        ) : null}

        {outroCtaBanner && (
          <Box
            className={clsx(baseStyles.gridFrameBottom, hasTestimonial && baseStyles.gridFrameTop)}
            marginBlockStart={hasTestimonial ? {narrow: 'spacious', regular: 64} : 'none'}
          >
            <FlexSuiteOutroCTA
              outroCtaHeading={outroCtaHeading}
              outroCtaDescription={outroCtaDescription}
              outroCtaOne={outroCtaOne}
              outroCtaTwo={outroCtaTwo}
            />
          </Box>
        )}

        {outroIncludeResources && (
          <FlexSuiteOutroResources outroResourcesHeading={outroResourcesHeading} outroResources={outroResources} />
        )}

        {hasBrandedDivider ? (
          <Box className={baseStyles.gridFrameBottom}>
            <Grid>
              <Grid.Column>
                <BrandedDivider />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {validFaqGroups.length > 0 && (
          <Box
            id="faq"
            className={validFaqGroups.length === 1 && !faqHeading ? styles.StandaloneFaq : undefined}
            paddingBlockStart={faqHeading ? undefined : {narrow: 'spacious', regular: 64}}
          >
            <FlexSuiteOutroFaqs
              groups={validFaqGroups}
              heading={faqHeading}
              answerRenderOptions={faqAnswerRenderOptions}
              headingPaddingInlineStart={{narrow: 'none', regular: 'spacious'}}
            />
          </Box>
        )}
      </Section>
    </Box>
  )
}
